import React from "react";

// SVG-ілюстрації товарів для заставки. Форми зроблені за мотивами реального
// асортименту: под-система (тип XROS), одноразка з дисплеєм (тип BC/Star),
// сольова рідина 30 мл та змінний картридж.

const PX = 2.6; // крок пікселя на дот-матричному дисплеї одноразки

const FLAME = [
  "..#..",
  "..##.",
  ".###.",
  ".####",
  "#####",
  "##.##",
  ".###.",
];

const BAR_ROWS = 8;

// Відблиск, що пробігає по корпусу
const Sweep = ({ clip, height, distance, delay }) => (
  <g clipPath={`url(#${clip})`}>
    <g transform="skewX(-18)">
      <rect
        className="sp-sweep"
        x="-40"
        y="0"
        width="26"
        height={height}
        fill="url(#sp-sweep)"
        style={{ "--sweep": `${distance}px`, animationDelay: `${delay}s` }}
      />
    </g>
  </g>
);

// Спільні градієнти (id глобальні для документа, тому визначаємо один раз)
export const SharedDefs = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <linearGradient id="sp-sweep" x1="0" x2="1">
        <stop offset="0%" stopColor="#fff" stopOpacity="0" />
        <stop offset="50%" stopColor="#fff" stopOpacity="0.38" />
        <stop offset="100%" stopColor="#fff" stopOpacity="0" />
      </linearGradient>
      {/* Циліндричне затінення: темні краї, світла смуга зліва */}
      <linearGradient id="sp-cyl" x1="0" x2="1">
        <stop offset="0%" stopColor="#000" stopOpacity="0.5" />
        <stop offset="14%" stopColor="#fff" stopOpacity="0.06" />
        <stop offset="30%" stopColor="#fff" stopOpacity="0.2" />
        <stop offset="52%" stopColor="#fff" stopOpacity="0" />
        <stop offset="86%" stopColor="#000" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
      </linearGradient>
      <linearGradient id="sp-mouth" x1="0" x2="1">
        <stop offset="0%" stopColor="#040507" />
        <stop offset="35%" stopColor="#353b4a" />
        <stop offset="55%" stopColor="#151922" />
        <stop offset="100%" stopColor="#040507" />
      </linearGradient>
      <linearGradient id="sp-chimney" x1="0" x2="1">
        <stop offset="0%" stopColor="#4b5563" />
        <stop offset="45%" stopColor="#e5e7eb" />
        <stop offset="65%" stopColor="#9ca3af" />
        <stop offset="100%" stopColor="#374151" />
      </linearGradient>
      <radialGradient id="sp-coil-glow">
        <stop offset="0%" stopColor="#fff7ed" />
        <stop offset="30%" stopColor="#fdba74" />
        <stop offset="60%" stopColor="#ea580c" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
      </radialGradient>
      <filter id="sp-blur-6" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="6" />
      </filter>
    </defs>
  </svg>
);

/* ───────────────────────── Под-система ───────────────────────── */

export const PodDevice = ({ percentRef, batteryRef }) => (
  <svg className="sp-svg" viewBox="0 0 80 270" aria-hidden="true">
    <defs>
      <linearGradient id="sp-pod-body" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#23996a" />
        <stop offset="55%" stopColor="#11643f" />
        <stop offset="100%" stopColor="#093523" />
      </linearGradient>
      <linearGradient id="sp-pod-liquid" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#99f6e4" />
        <stop offset="45%" stopColor="#2dd4bf" />
        <stop offset="100%" stopColor="#0f766e" />
      </linearGradient>
      <linearGradient id="sp-pod-ring" x1="0" x2="1">
        <stop offset="0%" stopColor="#4b5563" />
        <stop offset="35%" stopColor="#f3f4f6" />
        <stop offset="60%" stopColor="#9ca3af" />
        <stop offset="100%" stopColor="#374151" />
      </linearGradient>
      <pattern
        id="sp-leather"
        width="2.4"
        height="2.4"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <path d="M0 0 H2.4 M0 0 V2.4" stroke="#000" strokeOpacity="0.28" strokeWidth="0.5" />
      </pattern>
      <clipPath id="sp-pod-tank">
        <rect x="15" y="42" width="50" height="70" rx="8" />
      </clipPath>
      <clipPath id="sp-pod-shell">
        <path d="M21 46 L26 12 Q27 4 35 4 H45 Q53 4 54 12 L59 46 Z" />
        <rect x="13" y="38" width="54" height="78" rx="10" />
        <rect x="10" y="106" width="60" height="160" rx="15" />
      </clipPath>
    </defs>

    {/* Мундштук */}
    <path d="M21 46 L26 12 Q27 4 35 4 H45 Q53 4 54 12 L59 46 Z" fill="url(#sp-mouth)" />
    <path d="M31 7.5 H49" stroke="#fff" strokeOpacity="0.35" strokeWidth="1" strokeLinecap="round" />
    <path d="M27.5 14 L24.5 42" stroke="#fff" strokeOpacity="0.14" strokeWidth="1.4" strokeLinecap="round" />

    {/* Прозорий картридж */}
    <rect x="13" y="38" width="54" height="78" rx="10" fill="#c8ffeb" fillOpacity="0.07" />
    <g clipPath="url(#sp-pod-tank)">
      <rect x="37" y="38" width="6" height="62" fill="url(#sp-chimney)" />
      <g className="sp-level">
        <path
          className="sp-wave"
          d="M-10 66 q6.25 -3 12.5 0 t12.5 0 t12.5 0 t12.5 0 t12.5 0 t12.5 0 t12.5 0 t12.5 0 t12.5 0 t12.5 0 V120 H-10 Z"
          fill="url(#sp-pod-liquid)"
          fillOpacity="0.82"
        />
      </g>
      {/* Випаровувач з бавовняними вікнами */}
      <rect x="28" y="94" width="24" height="22" rx="3" fill="#1f2937" />
      <rect x="31" y="99" width="5" height="7" rx="1.2" fill="#d9a066" />
      <rect x="44" y="99" width="5" height="7" rx="1.2" fill="#d9a066" />
      <ellipse className="sp-coil-glow" cx="40" cy="102" rx="20" ry="15" fill="url(#sp-coil-glow)" />
      <g className="sp-pod-bubbles" fill="#ecfeff">
        {[
          [33, 0, 1.4],
          [37, 0.35, 1],
          [44, 0.15, 1.7],
          [47, 0.6, 1.1],
          [40, 0.85, 1.3],
          [35, 1.1, 0.9],
        ].map(([cx, delay, r]) => (
          <circle
            key={`${cx}-${delay}`}
            className="sp-bubble"
            cx={cx}
            cy="96"
            r={r}
            style={{ animationDelay: `${delay}s` }}
          />
        ))}
      </g>
    </g>
    <rect x="13" y="38" width="54" height="78" rx="10" fill="none" stroke="#fff" strokeOpacity="0.3" strokeWidth="1.2" />
    <rect x="18" y="46" width="3" height="56" rx="1.5" fill="#fff" fillOpacity="0.4" />
    <rect x="59.5" y="48" width="1" height="50" rx="0.5" fill="#fff" fillOpacity="0.15" />
    {/* Силіконова заглушка заправного отвору */}
    <rect x="64.5" y="64" width="3" height="16" rx="1.5" fill="#0b0f16" />

    {/* Корпус */}
    <rect x="10" y="106" width="60" height="160" rx="15" fill="url(#sp-pod-body)" />
    <rect x="10" y="106" width="60" height="160" rx="15" fill="url(#sp-leather)" />
    <rect x="10" y="106" width="60" height="160" rx="15" fill="url(#sp-cyl)" />
    <rect x="10" y="106" width="60" height="5" rx="2.5" fill="url(#sp-pod-ring)" />
    <rect x="34" y="113" width="12" height="2.4" rx="1.2" fill="#052e1c" />

    {/* Екран */}
    <rect x="21" y="122" width="38" height="32" rx="5" fill="#020405" stroke="#fff" strokeOpacity="0.1" strokeWidth="0.6" />
    <rect className="sp-screen-glow" x="21" y="122" width="38" height="32" rx="5" fill="#10b981" fillOpacity="0.18" />
    <text x="25" y="129.5" className="sp-svg-text" fontSize="3.6" fill="#6ee7b7">0.6Ω</text>
    <text x="55" y="129.5" className="sp-svg-text" fontSize="3.6" fill="#6ee7b7" textAnchor="end">MTL</text>
    <text x="40" y="142.5" className="sp-svg-text sp-screen-watt" fontSize="11" fontWeight="800" fill="#ffffff" textAnchor="middle">
      30W
    </text>
    <rect x="25" y="146" width="8.4" height="4.2" rx="0.8" fill="none" stroke="#a7f3d0" strokeWidth="0.6" />
    <rect x="33.4" y="147.2" width="0.9" height="1.8" rx="0.3" fill="#a7f3d0" />
    <rect ref={batteryRef} x="25.8" y="146.8" width="0" height="2.6" rx="0.4" fill="#34d399" />
    <text ref={percentRef} x="55" y="149.9" className="sp-svg-text" fontSize="4.8" fill="#d1fae5" textAnchor="end">
      0%
    </text>
    <path d="M22 123 L40 123 L28 153 L22 153 Z" fill="#fff" fillOpacity="0.04" />

    {/* Гравіювання */}
    <text
      x="40"
      y="214"
      className="sp-svg-text"
      fontSize="8"
      fontWeight="700"
      letterSpacing="3"
      fill="#fff"
      fillOpacity="0.2"
      textAnchor="middle"
      transform="rotate(-90 40 214)"
    >
      SMAK
    </text>

    <Sweep clip="sp-pod-shell" height={270} distance={210} delay={0.4} />
  </svg>
);

/* ───────────────────────── Одноразка ───────────────────────── */

export const Disposable = () => (
  <svg className="sp-svg" viewBox="0 0 110 180" aria-hidden="true">
    <defs>
      <linearGradient id="sp-disp-body" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#e9e3ff" />
        <stop offset="40%" stopColor="#b9a4ff" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
      <linearGradient id="sp-disp-mouth" x1="0" x2="1">
        <stop offset="0%" stopColor="#4c1d95" />
        <stop offset="40%" stopColor="#a78bfa" />
        <stop offset="100%" stopColor="#4c1d95" />
      </linearGradient>
      <clipPath id="sp-disp-shell">
        <rect x="12" y="30" width="86" height="146" rx="22" />
        <path d="M36 34 L39 12 Q40 5 48 5 H62 Q70 5 71 12 L74 34 Z" />
      </clipPath>
      <clipPath id="sp-disp-clip">
        <rect x="12" y="30" width="86" height="146" rx="22" />
      </clipPath>
    </defs>

    {/* Мундштук */}
    <path d="M36 34 L39 12 Q40 5 48 5 H62 Q70 5 71 12 L74 34 Z" fill="url(#sp-disp-mouth)" />
    <path d="M44 8 H66" stroke="#fff" strokeOpacity="0.45" strokeWidth="1" strokeLinecap="round" />

    {/* Матовий напівпрозорий корпус */}
    <rect x="12" y="30" width="86" height="146" rx="22" fill="url(#sp-disp-body)" fillOpacity="0.95" />
    <g clipPath="url(#sp-disp-clip)">
      {/* Внутрішній бак, що просвічує крізь корпус */}
      <rect x="32" y="46" width="46" height="112" rx="14" fill="#3b0764" fillOpacity="0.5" filter="url(#sp-blur-6)" />
      <ellipse
        className="sp-disp-glow"
        cx="55"
        cy="120"
        rx="30"
        ry="44"
        fill="#f5d0fe"
        filter="url(#sp-blur-6)"
      />
      {/* Бокова глянцева панель */}
      <rect x="80" y="30" width="18" height="146" fill="#2e1065" fillOpacity="0.55" />
      <rect x="80" y="30" width="0.8" height="146" fill="#fff" fillOpacity="0.35" />
      <rect x="12" y="30" width="86" height="146" fill="url(#sp-cyl)" opacity="0.7" />
    </g>
    <text
      x="89"
      y="103"
      className="sp-svg-text"
      fontSize="8"
      fontWeight="800"
      letterSpacing="2"
      fill="#fff"
      fillOpacity="0.8"
      textAnchor="middle"
      transform="rotate(90 89 103)"
    >
      SMAK 50K
    </text>

    {/* Дот-матричний дисплей */}
    <rect x="24" y="50" width="48" height="30" rx="7" fill="#0b0618" fillOpacity="0.92" stroke="#fff" strokeOpacity="0.12" strokeWidth="0.6" />
    {FLAME.map((row, r) =>
      [...row].map((_, c) => (
        <circle key={`d${r}-${c}`} cx={29 + c * PX} cy={55 + r * PX} r="0.95" fill="#24163d" />
      ))
    )}
    <g className="sp-flame">
      {FLAME.map((row, r) =>
        [...row].map((ch, c) =>
          ch === "#" ? (
            <circle
              key={`f${r}-${c}`}
              cx={29 + c * PX}
              cy={55 + r * PX}
              r="0.95"
              fill={r < 2 ? "#fde047" : "#fb923c"}
            />
          ) : null
        )
      )}
    </g>
    {[
      { x: 50, color: "#a3e635" },
      { x: 60, color: "#22d3ee" },
    ].map((bar) =>
      Array.from({ length: BAR_ROWS }, (_, r) =>
        [0, 1].map((col) => (
          <g key={`b${bar.x}-${r}-${col}`}>
            <circle cx={bar.x + col * PX} cy={55 + r * PX} r="0.95" fill="#24163d" />
            <circle
              className="sp-bar-px"
              cx={bar.x + col * PX}
              cy={55 + r * PX}
              r="0.95"
              fill={bar.color}
              style={{ animationDelay: `${(BAR_ROWS - 1 - r) * 0.11 + (bar.x === 60 ? 0.5 : 0)}s` }}
            />
          </g>
        ))
      )
    )}
    <rect x="49.4" y="76.2" width="4.2" height="2" rx="0.5" fill="none" stroke="#a3e635" strokeWidth="0.45" />
    <path d="M61.3 75.6 Q62.8 77.4 61.3 78.4 Q59.8 77.4 61.3 75.6 Z" fill="#22d3ee" />

    <text x="46" y="150" className="sp-svg-text" fontSize="7.5" fontWeight="800" fill="#fff" fillOpacity="0.9" textAnchor="middle">
      BLUE RAZZ
    </text>
    <text x="46" y="159" className="sp-svg-text" fontSize="4.4" fontWeight="600" letterSpacing="0.8" fill="#fff" fillOpacity="0.6" textAnchor="middle">
      50 000 PUFFS
    </text>

    {/* Глянцеві відблиски */}
    <rect x="17" y="42" width="4.5" height="118" rx="2.25" fill="#fff" fillOpacity="0.38" />
    <path d="M30 31.5 H80" stroke="#fff" strokeOpacity="0.5" strokeWidth="1" strokeLinecap="round" />

    <Sweep clip="sp-disp-shell" height={180} distance={210} delay={2.1} />
  </svg>
);

/* ───────────────────────── Рідина 30 мл ───────────────────────── */

export const LiquidBottle = () => (
  <svg className="sp-svg" viewBox="0 0 90 180" aria-hidden="true">
    <defs>
      <linearGradient id="sp-cap" x1="0" x2="1">
        <stop offset="0%" stopColor="#9ca3af" />
        <stop offset="22%" stopColor="#ffffff" />
        <stop offset="55%" stopColor="#e5e7eb" />
        <stop offset="100%" stopColor="#6b7280" />
      </linearGradient>
      <linearGradient id="sp-label" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fb923c" />
        <stop offset="55%" stopColor="#f43f5e" />
        <stop offset="100%" stopColor="#c026d3" />
      </linearGradient>
      <linearGradient id="sp-amber" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
      <clipPath id="sp-bottle-body">
        <path d="M16 96 Q16 82 30 82 H60 Q74 82 74 96 V166 Q74 176 64 176 H26 Q16 176 16 166 Z" />
      </clipPath>
      <clipPath id="sp-bottle-shell">
        <path d="M16 96 Q16 82 30 82 H60 Q74 82 74 96 V166 Q74 176 64 176 H26 Q16 176 16 166 Z" />
        <path d="M27 42 Q30 20 41 10 Q45 6 49 10 Q60 20 63 42 Z" />
        <rect x="22" y="40" width="46" height="40" rx="5" />
      </clipPath>
    </defs>

    {/* Кришка з захистом від дітей */}
    <path d="M27 42 Q30 20 41 10 Q45 6 49 10 Q60 20 63 42 Z" fill="url(#sp-cap)" />
    <rect x="22" y="40" width="46" height="38" rx="5" fill="url(#sp-cap)" />
    {Array.from({ length: 14 }, (_, i) => (
      <rect key={i} x={24.5 + i * 3} y="43" width="0.9" height="32" fill="#000" fillOpacity="0.09" />
    ))}
    <rect x="22" y="40" width="46" height="2" rx="1" fill="#fff" fillOpacity="0.6" />
    <rect x="24" y="77" width="42" height="6" rx="2" fill="#d1d5db" />

    {/* Пляшка з рідиною */}
    <path
      d="M16 96 Q16 82 30 82 H60 Q74 82 74 96 V166 Q74 176 64 176 H26 Q16 176 16 166 Z"
      fill="#fff"
      fillOpacity="0.16"
    />
    <g clipPath="url(#sp-bottle-body)">
      <g className="sp-level">
        <path
          className="sp-wave sp-wave--slow"
          d="M-20 98 q7.5 -2.5 15 0 t15 0 t15 0 t15 0 t15 0 t15 0 t15 0 t15 0 t15 0 V180 H-20 Z"
          fill="url(#sp-amber)"
          fillOpacity="0.6"
        />
      </g>

      {/* Етикетка */}
      <rect x="14" y="110" width="62" height="56" fill="url(#sp-label)" />
      <text x="45" y="123" className="sp-svg-text" fontSize="9" fontWeight="800" letterSpacing="0.6" fill="#fff" textAnchor="middle">
        SMAK LIQ
      </text>
      <text x="45" y="129.5" className="sp-svg-text" fontSize="3.8" fontWeight="600" letterSpacing="1.2" fill="#fff" fillOpacity="0.85" textAnchor="middle">
        NIC SALTS
      </text>
      <rect x="24" y="133" width="42" height="9" rx="4.5" fill="#fff" fillOpacity="0.92" />
      <text x="45" y="139.3" className="sp-svg-text" fontSize="5" fontWeight="800" fill="#be123c" textAnchor="middle">
        MANGO ICE
      </text>
      <text x="45" y="148" className="sp-svg-text" fontSize="3.8" fill="#fff" fillOpacity="0.85" textAnchor="middle">
        30 ml · 20 mg
      </text>
      <rect x="22" y="152" width="46" height="9.5" fill="#0a0a0a" />
      <rect x="25" y="154.6" width="40" height="1" fill="#fff" fillOpacity="0.75" />
      <rect x="25" y="157.6" width="28" height="1" fill="#fff" fillOpacity="0.55" />

      <rect x="14" y="80" width="62" height="100" fill="url(#sp-cyl)" opacity="0.85" />
    </g>
    <path
      d="M16 96 Q16 82 30 82 H60 Q74 82 74 96 V166 Q74 176 64 176 H26 Q16 176 16 166 Z"
      fill="none"
      stroke="#fff"
      strokeOpacity="0.32"
      strokeWidth="1"
    />
    <rect x="21" y="90" width="3.5" height="78" rx="1.75" fill="#fff" fillOpacity="0.35" />
    <rect x="22" y="40" width="46" height="38" rx="5" fill="url(#sp-cyl)" opacity="0.5" />

    <Sweep clip="sp-bottle-shell" height={180} distance={210} delay={3.4} />
  </svg>
);

/* ───────────────────────── Картридж ───────────────────────── */

export const Cartridge = () => (
  <svg className="sp-svg" viewBox="0 0 50 100" aria-hidden="true">
    <defs>
      <linearGradient id="sp-cart-liquid" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fbcfe8" />
        <stop offset="45%" stopColor="#f472b6" />
        <stop offset="100%" stopColor="#be185d" />
      </linearGradient>
      <clipPath id="sp-cart-tank">
        <rect x="8" y="24" width="34" height="58" rx="6" />
      </clipPath>
    </defs>
    <path d="M14 26 L17 8 Q18 3 23 3 H27 Q32 3 33 8 L36 26 Z" fill="url(#sp-mouth)" />
    <path d="M20 5.5 H30" stroke="#fff" strokeOpacity="0.35" strokeWidth="0.8" strokeLinecap="round" />
    <rect x="7" y="22" width="36" height="64" rx="7" fill="#fff" fillOpacity="0.08" />
    <g clipPath="url(#sp-cart-tank)">
      <rect x="22.5" y="22" width="5" height="54" fill="url(#sp-chimney)" />
      <g className="sp-level">
        <path
          className="sp-wave sp-wave--fast"
          d="M-6 46 q4.5 -2 9 0 t9 0 t9 0 t9 0 t9 0 t9 0 t9 0 t9 0 V90 H-6 Z"
          fill="url(#sp-cart-liquid)"
          fillOpacity="0.85"
        />
      </g>
      <rect x="15" y="70" width="20" height="16" rx="2.5" fill="#1f2937" />
      <rect x="18" y="74" width="4" height="5" rx="1" fill="#d9a066" />
      <rect x="28" y="74" width="4" height="5" rx="1" fill="#d9a066" />
    </g>
    <rect x="7" y="22" width="36" height="64" rx="7" fill="none" stroke="#fff" strokeOpacity="0.32" strokeWidth="1" />
    <rect x="11" y="28" width="2.6" height="44" rx="1.3" fill="#fff" fillOpacity="0.4" />
    <rect x="10" y="84" width="30" height="9" rx="3" fill="#111827" />
    <circle cx="19" cy="88.5" r="1.8" fill="#eab308" />
    <circle cx="31" cy="88.5" r="1.8" fill="#eab308" />
  </svg>
);
